import { Loading } from "@/components/Loading";
import { FileType, type YFileSystemObject } from "@/utils/yjs/FileSystem";
import { createId } from "@paralleldrive/cuid2";
import type { WebContainer } from "@webcontainer/api";
import type { Terminal } from "@xterm/xterm";
import {
	type ReactNode,
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import * as Y from "yjs";
import { z } from "zod";
import { useEditorState } from "./EditorStateContext";
import { terminalStdout } from "./terminalObservables";
import { type Engine, WebContainerEngine } from "./test";

export const CommandSchema = z.object({
	id: z.string().cuid2(),
	name: z.string().optional(),
	command: z.string(),
	args: z.array(z.string()),
	options: z.any().optional(),
});

export type Command = z.infer<typeof CommandSchema>;

interface WebContainerContextType {
	webcontainerInstance: WebContainer | null;
	commands: Record<string, Command>;
	setCommand: (value: Command, key?: string) => string;
	getCommands: () => Command[];
	runCommand: (command: Command, terminal: Terminal) => Promise<number>;
	setFileContents: (path: string, newContents: string) => void;
}

const WebContainerContext = createContext<WebContainerContextType | undefined>(
	undefined,
);

export const WebContainerProvider = ({ children }: { children: ReactNode }) => {
	const [webcontainerInstance, setWebcontainerInstance] =
		useState<WebContainer | null>(null);
	const { yfs, ydoc } = useEditorState();
	const [commands, setCommands] = useState<Record<string, Command>>({});

	const engine = useRef<Engine>(new WebContainerEngine());

	// Initialize the webcontainer only once on mount
	// useEffect(() => {
	// 	const initializeWebContainer = async () => {
	// 		// Initialize the engine
	// 		await engine.current.initialize(yfs);
	// 		setWebcontainerInstance(engine.current.instance);
	// 	};
	//
	// 	initializeWebContainer();
	//
	// 	return () => {
	// 		engine.current.teardown();
	// 	};
	// }, [yfs]);
	//
	// Run a command and pipe the output to the terminal
	const runCommand = async (command: Command, terminal: Terminal) => {
		// Initialize the web container if it doesn't exist
		if (!engine.current.instance) {
			await engine.current.initialize(yfs);
			setWebcontainerInstance(engine.current.instance);
		}

		const stdout = new WritableStream({
			write(data) {
				// Write that to the terminal stdout observable
				terminalStdout.next({
					content: data,
					// Came from inside the web container, so local
					remote: false,
				});
			},
		});

		return engine.current.runCommand(command, stdout);
	};

	// Helper function to update the contents of a specific file
	const setFileContents = (path: string, newContents: string) => {
		engine.current.writeFile(path, newContents);
	};

	// If any file or directory is changed in yjs, update the webcontainer filesystem
	useEffect(() => {
		const observer = async (event: Y.YMapEvent<YFileSystemObject>) => {
			for (const key of event.keysChanged) {
				try {
					const type = yfs.getType(key);
					if (type === FileType.File) {
						const newValue = yfs.readFile(key).contents?.toString();
						await engine.current.writeFile(key, newValue ?? "");
					} else if (type === FileType.Directory) {
						await engine.current.writeDirectory(key);
					}
				} catch (error) {
					if (
						!(error as Error).message.includes("File or Directory not found")
					) {
						throw error;
					}

					// Remove the file from the engine filesystem
					await engine.current.rm(key);
					yfs.rm(key);
				}
			}
		};

		yfs.fsDoc.observe(observer);

		return () => {
			yfs.fsDoc.unobserve(observer);
		};
	}, [yfs]);

	// If the commands change, update the state
	useEffect(() => {
		const observer = async (event: Y.YMapEvent<Y.Text>) => {
			let newCommands: Record<string, Command> = commands;
			for (const key of event.keysChanged) {
				const newValue = ydoc.getMap<Y.Text>("commands").get(key);

				if (!newValue) {
					const { [key]: _, ...rest } = newCommands;
					newCommands = rest;
					break;
				}

				const { data } = CommandSchema.safeParse(newValue.toString());
				if (data) {
					newCommands = {
						[key]: data,
						...newCommands,
					};
				}
			}
			setCommands(newCommands);
		};
		ydoc.getMap<Y.Text>("commands").observe(observer);

		return () => {
			ydoc.getMap<Y.Text>("commands").unobserve(observer);
		};
	});

	// Set initial commands object
	useEffect(() => {
		// Set initial commands
		const initialCommands: Command[] = [];
		for (const key of ydoc.getMap<Y.Text>("commands").keys()) {
			const command = ydoc.getMap<Y.Text>("commands").get(key);
			if (!command) {
				continue;
			}
			const { data } = CommandSchema.safeParse(command.toString());
			if (data) {
				initialCommands.push(data);
			}
		}
		setCommands(
			Object.fromEntries(
				initialCommands.map((command) => [command.id, command]),
			),
		);
	}, [ydoc]);

	return (
		<WebContainerContext.Provider
			value={{
				webcontainerInstance: webcontainerInstance,
				setFileContents,
				runCommand,
				commands,
				setCommand: (value, key) => {
					const myKey = key ?? createId();
					ydoc
						.getMap<Y.Text>("commands")
						.set(myKey, new Y.Text(JSON.stringify(value)));
					return myKey;
				},
				getCommands: () => Object.values(commands),
			}}
		>
			{children}
		</WebContainerContext.Provider>
	);
};

export const useWebContainer = () => {
	const context = useContext(WebContainerContext);
	if (context === undefined) {
		throw new Error(
			"useWebContainer must be used within a WebContainerProvider",
		);
	}
	return context;
};
